import { ICommonSettings, IWorkflowEngineBaseUrlSettings } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { IWithSDProps } from "../../../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigWorkflowEngineBaseUrlSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingConfig?: IWorkflowEngineBaseUrlSettings;
}

export interface IConfigWorkflowEngineBaseUrlSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingWFEngineBaseUrlId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigWorkflowEngineBaseUrlSettings extends React.PureComponent<
  IConfigWorkflowEngineBaseUrlSettingsProps,
  IConfigWorkflowEngineBaseUrlSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigWorkflowEngineBaseUrlSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle"
    };
  }

  componentDidMount() {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigWorkflowEngineBaseUrlSettingsProps & IWithSDProps) {
    if (prevProps.editingWFEngineBaseUrlId !== this.props.editingWFEngineBaseUrlId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = () => {
    if (!this.props.editingWFEngineBaseUrlId) return;

    const editingConfig = this.props.commonSettings.workflowEngineBaseUrlSettings?.find(
      (x) => x.id === this.props.editingWFEngineBaseUrlId
    );

    if (!editingConfig) return;
    this.setState({ editingConfig }, () => {
      this.formRef.current?.resetFields();
      this.formRef.current?.setFieldsValue(editingConfig);
    });
  };

  existingNameValidator = async (_rule, value) => {
    const wfeBaseUrlSettings = this.props.commonSettings?.workflowEngineBaseUrlSettings;
    if (!wfeBaseUrlSettings) return;

    const isSettingAlreadyExist = wfeBaseUrlSettings.some(
      (x) => (x.name === value && x.id !== this.props.editingWFEngineBaseUrlId) || value === "Default"
    );

    if (isSettingAlreadyExist) throw new Error("Workflow Engine Base Url Name. is already exist.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();
    let obj: IWorkflowEngineBaseUrlSettings;
    obj = {
      id: Guid.create().toString(),
      name: data.name,
      baseUrl: data.baseUrl?.trim()
    };
    if (this.props.editingWFEngineBaseUrlId) {
      obj.id = this.props.editingWFEngineBaseUrlId;
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "workflowEngineBaseUrlSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "workflowEngineBaseUrlSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteSetting = () => {
    if (!this.props.editingWFEngineBaseUrlId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingWFEngineBaseUrlId || "",
      configKey: "workflowEngineBaseUrlSettings",

      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Workflow Engine Base Url setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Workflow Engine Base Url setting"
        secondHeader="Workflow Engine Base Url setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingWFEngineBaseUrlId ? this.deleteSetting : undefined}
            deleteTestId="workflowEngineBaseUrlSettingDeleteButton"
            submitTestId="workflowEngineBaseUrlSettingCreateButton"
            submitLabel={this.props.editingWFEngineBaseUrlId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configWorkflowEngineBaseUrlForm"
          />
        }
      >
        <Form
          id="configWorkflowEngineBaseUrlForm"
          name="wfEngineBaseUrlSettings"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody
            description={this.props.getSectionDescription("appsettings_workflowenginebaseurlsetting_description")}
          >
            <ConfigField label="Name" required tooltipContentKey="appsettings_workflow_name">
              <Form.Item
                name="name"
                rules={[
                  { required: true, message: "Please enter a Workflow Engine Base Url Name." },
                  { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Workflow Engine Base Url Name is invalid" },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" data-testid="workflowEngineBaseUrlSettingNameInput" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField label="Base url" required tooltipContentKey="appsettings_workflow_baseurl">
              <Form.Item
                name="baseUrl"
                rules={[
                  { required: true, message: "Please enter a Workflow Engine Base Url." },
                  {
                    pattern:
                      /^https?:\/\/((([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,15})|(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}))(:\d{1,5})?(\/\S*)?(\?\S*)?$/,
                    message: "Workflow Engine Base Url is not valid."
                  }
                ]}
              >
                <KuikaInput size="md" data-testid="workflowEngineBaseUrlSettingBaseUrlInput" />
              </Form.Item>
            </ConfigField>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
